import { getApiBaseUrl, getMerchantClient } from '@beatq/sdk-client';
import {
  getAccessToken,
  mapErrorCode,
  readDataFromEnvelope,
  readListFromEnvelope,
  readObjectFieldFromEnvelope,
} from '@beatq/shared';

type ApiErr = { error?: { code: string; message: string } };

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

function merchantApiUrl(path: string): string {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

async function merchantFetch(path: string, init?: RequestInit) {
  const token = getAccessToken();
  const res = await fetch(merchantApiUrl(path), {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(sdkErrorMessage(json));
  return json;
}

export type MerchantBusiness = {
  id?: string;
  legalName?: string;
  tradingName?: string;
  billingEmail?: string;
  description?: string | null;
  supportPhoneE164?: string | null;
  status?: string;
  verificationState?: string;
  logoUrl?: string | null;
  listingImageUrl?: string | null;
  heroImageUrl?: string | null;
  primaryColour?: string | null;
  verticals?: string[] | null;
  vertical?: string | null;
  verticalCode?: string | null;
  primaryVertical?: string | null;
  branding?: {
    logoUrl?: string | null;
    listingImageUrl?: string | null;
    heroImageUrl?: string | null;
    primaryColour?: string | null;
  } | null;
  settings?: {
    branding?: {
      logoUrl?: string | null;
      listingImageUrl?: string | null;
      heroImageUrl?: string | null;
      primaryColour?: string | null;
    } | null;
  } | null;
};

export type TenantStatus = {
  businessId?: string;
  status?: string;
  verificationState?: string;
  mayAcceptOrders?: boolean;
  mayReceivePayout?: boolean;
  mayAppearInListings?: boolean;
};

export type CommercialTermsRow = {
  id?: string;
  version?: string | null;
  commissionRateBps?: number | null;
  commissionBps?: number | null;
  serviceFeeRateBps?: number | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  acceptedAt?: string | null;
  pending?: boolean;
};

export type VatRegistration = {
  vatRegistered?: boolean;
  vatNumber?: string | null;
  registered?: boolean;
  registrationNumber?: string | null;
};

export function businessFromEnvelope(envelope: unknown): MerchantBusiness | undefined {
  const business =
    readObjectFieldFromEnvelope<MerchantBusiness>(envelope, ['business']) ??
    readDataFromEnvelope<MerchantBusiness>(envelope);
  if (!business) return undefined;
  const branding = business.branding ?? business.settings?.branding;
  return {
    ...business,
    logoUrl: business.logoUrl ?? branding?.logoUrl ?? null,
    listingImageUrl: business.listingImageUrl ?? branding?.listingImageUrl ?? null,
    heroImageUrl: business.heroImageUrl ?? branding?.heroImageUrl ?? null,
    primaryColour: business.primaryColour ?? branding?.primaryColour ?? null,
  };
}

export function tenantStatusFromEnvelope(envelope: unknown): TenantStatus | undefined {
  return (
    readObjectFieldFromEnvelope<TenantStatus>(envelope, ['status']) ??
    readDataFromEnvelope<TenantStatus>(envelope)
  );
}

export function commercialTermsFromEnvelope(envelope: unknown): CommercialTermsRow[] {
  const single = readObjectFieldFromEnvelope<CommercialTermsRow>(envelope, ['terms']);
  if (single && !Array.isArray(single) && (single.id || single.commissionRateBps != null)) {
    return [single];
  }
  return readListFromEnvelope<CommercialTermsRow>(envelope, ['terms', 'items']);
}

export function vatFromEnvelope(envelope: unknown): VatRegistration | undefined {
  const vat =
    readObjectFieldFromEnvelope<Record<string, unknown>>(envelope, ['vat', 'vatRegistration']) ??
    readDataFromEnvelope<Record<string, unknown>>(envelope);
  if (!vat || typeof vat !== 'object') return undefined;
  if (!('vatRegistered' in vat) && !('vatNumber' in vat) && !('registered' in vat)) {
    return undefined;
  }
  const vatRegistered = Boolean(vat.vatRegistered ?? vat.registered);
  const vatNumber =
    typeof vat.vatNumber === 'string'
      ? vat.vatNumber
      : typeof vat.registrationNumber === 'string'
        ? vat.registrationNumber
        : null;
  return {
    vatRegistered,
    vatNumber,
    registered: vatRegistered,
    registrationNumber: vatNumber,
  };
}

export async function getBusiness() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/business');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateBusiness(body: {
  tradingName?: string;
  description?: string;
  supportPhoneE164?: string;
  billingEmail?: string;
}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH('/merchant/v1/business', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateBusinessBranding(body: {
  logoUrl?: string;
  listingImageUrl?: string;
  heroImageUrl?: string;
  primaryColour?: string;
  description?: string;
}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PUT('/merchant/v1/business/branding', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export type BusinessBrandingImagePurpose = 'BUSINESS_HERO' | 'BRANCH_LISTING' | 'LOGO';

export function resolveBrandingImagePreviewSrc(url?: string | null): string | null {
  if (!url?.trim()) return null;
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('data:')) return trimmed;
  const base = getApiBaseUrl().replace(/\/$/, '');
  if (!base) return trimmed;
  return `${base}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
}

export function brandingMediaContentPath(assetId: string): string {
  return `/customer/v1/media/${assetId}/content`;
}

/** Upload branding image → READY media → relative customer content URL. */
export async function uploadBusinessBrandingImage(
  file: File,
  purpose: BusinessBrandingImagePurpose,
): Promise<string> {
  const token = getAccessToken();
  const sessionJson = await merchantFetch('/merchant/v1/media/upload-urls', {
    method: 'POST',
    body: JSON.stringify({
      contentType: file.type || 'image/jpeg',
      sizeBytes: Math.max(file.size, 1),
      purpose,
    }),
  });
  const session =
    (sessionJson as { data?: { assetId?: string; uploadUrl?: string } })?.data ??
    (sessionJson as { assetId?: string; uploadUrl?: string });
  const assetId = session?.assetId;
  if (!assetId) throw new Error('Upload session missing assetId');

  const uploadUrl = session.uploadUrl;
  if (uploadUrl && !/storage\.stub\.beatqeats/i.test(uploadUrl)) {
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    if (!put.ok) throw new Error('File upload failed. Try again.');
  } else {
    const res = await fetch(
      merchantApiUrl(`/merchant/v1/media/${encodeURIComponent(assetId)}/content`),
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'X-Original-Filename': file.name,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: file,
      },
    );
    if (!res.ok) throw new Error('File upload failed. Try again.');
  }

  await merchantFetch(`/merchant/v1/media/${encodeURIComponent(assetId)}/confirm`, {
    method: 'POST',
    body: '{}',
  });

  return brandingMediaContentPath(assetId);
}

/** @deprecated Use uploadBusinessBrandingImage(file, 'BUSINESS_HERO') */
export async function uploadBusinessStorefrontImage(file: File): Promise<string> {
  return uploadBusinessBrandingImage(file, 'BUSINESS_HERO');
}

/** Not yet in OpenAPI client — raw fetch until schema regenerates. */
export async function getTenantStatus() {
  return merchantFetch('/merchant/v1/business/status');
}

export async function getCommercialTerms() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/business/commercial-terms');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

/** Not yet in OpenAPI client — raw fetch until schema regenerates. */
export async function getCommercialTermsHistory() {
  return merchantFetch('/merchant/v1/business/commercial-terms/history');
}

export async function getVatRegistration() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/business/vat-registration');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateVatRegistration(body: { vatRegistered: boolean; vatNumber?: string }) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PUT('/merchant/v1/business/vat-registration', {
    body: {
      vatRegistered: body.vatRegistered,
      vatNumber: body.vatNumber,
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}
